import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css'; // Re-add for legacy
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Re-add for legacy
import { useAuth } from '../AuthContext';

function MemberUsageLog() {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isStripeFilter, setIsStripeFilter] = useState(true); // Default to Membership Transactions Only (stripe)
  const gridRef = useRef(null);
  const { user, fetchWrapper } = useAuth();

  const columnDefs = useMemo(() => [
    {
      field: 'action',
      headerName: 'Transaction',
      filter: 'agTextColumnFilter',
      sortable: true,
      flex: 1.8,
      minWidth: 200
    },
    {
      field: 'details',
      headerName: 'DETAILS',
      filter: 'agTextColumnFilter',
      sortable: true,
      flex: 2.4,
      minWidth: 300
    },
    {
      field: 'created_at',
      headerName: 'Date',
      filter: 'agDateColumnFilter',
      sortable: true,
      flex: 1.5,
      minWidth: 150,
      valueFormatter: params => params.value ? new Date(params.value).toLocaleString() : 'N/A'
    },
    // Images: Signed whole number (negative debits, positive credits)
    {
      field: 'images',
      headerName: 'Images',
      filter: 'agNumberColumnFilter',
      sortable: true,
      valueFormatter: params => {
        if (params.value !== null && params.value !== undefined) {
          return params.value.toFixed(0); // Whole number
        }
        return '0';
      },
      cellStyle: params => ({ color: params.value < 0 ? 'red' : 'green' }),
      aggFunc: 'sum',
      minWidth: 120
    },
    // Purchases: Dollars (positive); blank for non-stripe
    {
      field: 'purchases',
      headerName: 'Purchases',
      filter: 'agNumberColumnFilter',
      sortable: true,
      valueFormatter: params => {
        if (params.value !== null && params.value !== undefined && params.value !== '') {
          return `$${params.value.toFixed(2)}`;
        }
        return ''; // Blank for non-stripe (null/undefined/'')
      },
      cellStyle: params => ({ color: params.value < 0 ? 'red' : 'green' }),
      aggFunc: 'sum',
      minWidth: 120
    },
    {
      field: 'source',
      headerName: 'Source',
      hide: true,
      filter: 'agTextColumnFilter'
    }
  ], []);

  const defaultColDef = useMemo(() => ({
    flex: 1,
    minWidth: 100,
    filter: true,
    resizable: true
  }), []);

  const autoGroupColumnDef = useMemo(() => ({
    headerName: 'Group',
    minWidth: 200,
    field: 'action',
    rowGroup: true,
    hide: true
  }), []);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!user) {
          setError('Please sign in.');
          console.log('No user logged in');
          return;
        }
        setUserId(user.id);
        console.log('Fetching logs for user_id:', user.id);
        // Fetch logs via proxy
        const logResponse = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/get_member_usage_log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ p_user_id: user.id }),
        });
        if (!logResponse) {
          // Wrapper handled auth error (e.g., logout/redirect); no need for further action
          return;
        }
        const logData = await logResponse.json();
        if (logData.error) {
          throw new Error('Logs fetch failed: ' + logData.error);
        }
        // Process log data with updated details, images (signed), and purchases
       // Process log data with updated details, images (signed), and purchases
const processedData = logData.map(log => {
  const meta = log.meta || {};
  const action = log.action || '';
  const source = log.source || '';
  const transactionType = log.transaction_type || '';
  const tier = meta.tier || 'N/A';
  let details = 'N/A';
  let images = 0; // Default 0
  let purchases = ''; // Default blank for non-stripe
  if (source.toLowerCase() === 'stripe') {
    if (action.includes('Cancellation')) {
      // NEW: Parse meta.cancel_at for cancellation details
      const cancelAt = meta.cancel_at;
      if (cancelAt) {
        const cancelDate = new Date(cancelAt);
        details = `Cancelling on ${cancelDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      } else {
        details = 'Subscription Cancellation (date not set)';
      }
      images = 0; // No credits on cancel
      purchases = 0; // No financial change
    } else {
      // Existing Stripe purchases/renewals
      details = tier;
      images = parseInt(log.amount) || 0; // Credits earned
      purchases = parseFloat(log.financial_amount) || 0; // Dollars spent
    }
  } else if (source.toLowerCase() === 'client_upload') {
    // Signed: Negative for debits, positive for credits
    const rawAmount = parseInt(log.amount) || 0;
    images = transactionType.toLowerCase() === 'image_debit' ? -rawAmount : rawAmount;
    purchases = ''; // Explicit blank for client_upload
    if (transactionType.toLowerCase() === 'image_debit') {
      // Extract sale and section from meta data
      const saleName = meta.salename || 'Unknown Sale';
      const sectionName = meta['sales section'] || 'Unknown Section';
      details = `${saleName}/${sectionName}`;
    } else if (transactionType.toLowerCase() === 'image_credit') {
      // Fixed statement as per instructions
      details = '10 deleted items = 1 image credit';
    } else {
      // Fallback for other client_upload actions
      details = meta.filename || meta.title || action || 'Upload activity';
    }
  } else {
    // Unexpected source
    details = action || 'Unknown';
  }
  return {
    ...log,
    details,
    images,
    purchases,
  };
});
        setRowData(processedData || []);
      } catch (err) {
        setError(`Error loading logs: ${err.message}`);
        console.error('Fetch logs error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [user, fetchWrapper]);

  useEffect(() => {
    if (gridRef.current?.api) {
      const filterModel = isStripeFilter ? {
        source: { type: 'equals', filter: 'stripe' } // Membership Transactions Only
      } : {}; // FIXED: No filter for ALL transactions (stripe + non-stripe)
      gridRef.current.api.setFilterModel(filterModel);
    }
  }, [isStripeFilter, rowData]); // Add rowData to deps to re-apply after data loads

  const onGridReady = (params) => {
    console.log('Grid ready, row data:', JSON.stringify(rowData, null, 2));
  };

  const handleExportCsv = () => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: 'member_usage_log.csv',
        prependContent: 'Member Usage Log - Scucci Systems\nGenerated on ' + new Date().toLocaleString() + '\n'
      });
    } else {
      setError('Error: Grid not ready for export.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <div className="flex-grow p-4">
        <div className="max-w-screen-xl mx-auto bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-4 text-center">Member Usage Log - Scucci Systems</h2>
          {loading && <p className="text-gray-500 text-center">Loading usage logs...</p>}
          {error && <p className="text-red-500 mb-4 text-center">{error}</p>}
          {rowData.length === 0 && !loading && !error && (
            <p className="text-gray-500 mt-4 text-center">No usage logs found for user {userId || 'unknown'}.</p>
          )}
          <div className="flex justify-end mb-4 space-x-2">
            <button
              onClick={handleExportCsv}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition text-sm"
            >
              Export to CSV
            </button>
            <button
              onClick={() => setIsStripeFilter(!isStripeFilter)}
              className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition text-sm"
            >
              {isStripeFilter ? 'View All Transactions' : 'View Membership Transactions Only'}
            </button>
          </div>
          <div className="ag-theme-alpine" style={{ height: 600, width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              autoGroupColumnDef={autoGroupColumnDef}
              enableRangeSelection={true}
              animateRows={true}
              groupIncludeFooter={true}
              groupIncludeTotalFooter={true}
              onGridReady={onGridReady}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default MemberUsageLog;